import { useState } from 'react';
import { LeaveRequestStatus } from '../../../commonTypes/leaveRequest.types';
import {
    useHttpApproveLeaveRequest,
    useHttpRejectLeaveRequest,
    useHttpDeleteLeaveRequest,
    useHttpRejectMedicalLeaveRequest,
    useHttpApproveMedicalLeaveRequest,
} from '../../../hooks';
import {
    editUpcomingLeaveRequest,
    updateLeaveRequestActionStatus,
    updateMedicalLeaveRequestActionStatus,
    selectLeaveRequestActionStatuses,
    selectMedicalLeaveRequestActionStatuses,
} from '../../../store/slices/user-profile.slice';
import { Button } from '../../Common';
import { Modal, YesNo } from '../../Common';
import RejectLeaveRequest from '../../modalContent/RejectLeaveRequest';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../../hooks/useTranslation';
import { DateTime } from 'luxon';

interface NotificationInfoProps {
    id: string;
    type: string;
    title: string;
    message: React.ReactNode;
    isAction?: boolean;
    isRead?: boolean;
    onClick: () => void;
    isFormattedMessage?: boolean;
    leaveRequestId: string;
    medicalLeaveRequestId: string;
    originalTitle?: string;
    fromDate?: DateTime;
}

const NotificationInfo: React.FC<NotificationInfoProps> = ({
    type,
    title,
    message,
    isAction,
    isRead,
    isFormattedMessage,
    onClick,
    leaveRequestId,
    medicalLeaveRequestId,
    originalTitle,
    fromDate,
}) => {
    const [isRejectVisible, setIsRejectVisibility] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const { t } = useTranslation();
    const [isWithdrawn, setIsWithdrawn] = useState(false);

    const leaveRequestStatuses = useSelector(selectLeaveRequestActionStatuses);
    const medicalLeaveRequestStatuses = useSelector(selectMedicalLeaveRequestActionStatuses);

    const currentStatus =
        originalTitle === 'MEDICAL_LEAVE_REQUEST_CREATION'
            ? medicalLeaveRequestStatuses[medicalLeaveRequestId]
            : leaveRequestStatuses[leaveRequestId];

    const dispatch = useDispatch();

    const { approveLeaveRequest: execApprove } = useHttpApproveLeaveRequest();
    const { rejectLeaveRequest: execReject } = useHttpRejectLeaveRequest();
    const { deleteLeaveRequest: execDelete } = useHttpDeleteLeaveRequest();

    const { approveMedicalLeaveRequest: execApproveMedical } = useHttpApproveMedicalLeaveRequest();
    const { rejectMedicalLeaveRequest: execRejectMedical } = useHttpRejectMedicalLeaveRequest();

    const isMedicalLeaveRequest = originalTitle === 'MEDICAL_LEAVE_REQUEST_CREATION';
    const isRegularLeaveRequest = originalTitle === 'LEAVE_REQUEST_CREATION';
    const isApprovalNotification = originalTitle === 'LEAVE_REQUEST_APPROVE';

    const approveLeaveRequest = () => {
        setIsProcessing(true);

        if (isMedicalLeaveRequest) {
            dispatch(
                updateMedicalLeaveRequestActionStatus({
                    medicalLeaveRequestId,
                    status: LeaveRequestStatus.APPROVED,
                })
            );

            execApproveMedical(medicalLeaveRequestId, (id: string) => {
                dispatch(
                    editUpcomingLeaveRequest({
                        id,
                        newStatus: LeaveRequestStatus.APPROVED,
                    })
                );
            });
        } else {
            dispatch(
                updateLeaveRequestActionStatus({
                    leaveRequestId,
                    status: LeaveRequestStatus.APPROVED,
                })
            );

            execApprove(leaveRequestId, (id: string) => {
                dispatch(
                    editUpcomingLeaveRequest({
                        id,
                        newStatus: LeaveRequestStatus.APPROVED,
                    })
                );
            });
        }
    };

    const rejectLeaveRequest = (id: string, rejectReason: string) => {
        if (isMedicalLeaveRequest) {
            dispatch(
                updateMedicalLeaveRequestActionStatus({
                    medicalLeaveRequestId: id,
                    status: LeaveRequestStatus.REJECTED,
                })
            );

            execRejectMedical(
                medicalLeaveRequestId,
                (returnedId: string, reason?: string) => {
                    dispatch(
                        editUpcomingLeaveRequest({
                            id: returnedId,
                            newStatus: LeaveRequestStatus.REJECTED,
                            rejectReason: reason,
                        })
                    );
                },
                rejectReason
            );
        } else {
            dispatch(
                updateLeaveRequestActionStatus({
                    leaveRequestId: id,
                    status: LeaveRequestStatus.REJECTED,
                })
            );

            execReject(
                leaveRequestId,
                (returnedId: string) => {
                    dispatch(
                        editUpcomingLeaveRequest({
                            id: returnedId,
                            newStatus: LeaveRequestStatus.REJECTED,
                            rejectReason,
                        })
                    );
                },
                rejectReason
            );
        }
    };

    const isInThePast = () => {
        if (!fromDate) return false;

        const dateTime = DateTime.isDateTime(fromDate) ? fromDate : DateTime.fromJSDate(new Date(fromDate));

        return dateTime.startOf('day') < DateTime.now().startOf('day');
    };

    const deleteLeaveRequest = () => {
        setIsProcessing(true);
        execDelete(leaveRequestId, {
            onSuccess: () => {
                setIsProcessing(false);
                setIsWithdrawn(true);
            },
        });
    };

    const formattedMessage =
        isFormattedMessage && typeof message === 'string'
            ? message.split('\n').map((line, index) => {
                  const [label, value] = line.includes(': ') ? line.split(': ') : [null, line];
                  return (
                      <div key={index}>
                          {label ? (
                              <>
                                  <span className="notifications__item-message-key">{label}:</span> {value}
                              </>
                          ) : (
                              value
                          )}
                      </div>
                  );
              })
            : message;

    const showActions = isAction && !isProcessing && !currentStatus;

    return (
        <div className={`notifications__item ${isRead ? 'read' : 'unread'}`} onClick={!isRead ? onClick : undefined}>
            <div className="notifications__item-wrapper">
                <div className="notifications__item-header">
                    <img src={type} alt="notification-type" />
                    <p className="notifications__item-title">{title}</p>
                </div>
                <div className="notifications__item-content">
                    <div className="notifications__item-message">{formattedMessage}</div>
                </div>
                {showActions && (isRegularLeaveRequest || isMedicalLeaveRequest) && (
                    <div className="notifications__item-actions">
                        <Button
                            text={t('common.approve')}
                            variant="contained"
                            className="notifications__item-actions-button"
                            textClassName="notifications__item-actions-text"
                            onClick={approveLeaveRequest}
                        />
                        <Button
                            text={t('common.reject')}
                            variant="bordered"
                            className="notifications__item-actions-button"
                            textClassName="notifications__item-actions-text"
                            onClick={() => setIsRejectVisibility(true)}
                        />
                    </div>
                )}
                {isAction && isApprovalNotification && !isWithdrawn && !isInThePast() && (
                    <div className="notifications__item-actions">
                        <Button
                            text={t('common.withdraw')}
                            variant="text"
                            textClassName="notifications__item-actions-text"
                            onClick={() => setDeleteModalVisible(true)}
                        />
                    </div>
                )}
                <Modal isVisible={isRejectVisible} closeModal={() => setIsRejectVisibility(false)}>
                    <RejectLeaveRequest id={leaveRequestId} rejectLeaveRequest={rejectLeaveRequest} close={() => setIsRejectVisibility(false)} />
                </Modal>
                <Modal isVisible={isDeleteModalVisible} closeModal={() => setDeleteModalVisible(false)}>
                    <YesNo
                        title={t('common.areYouSure')}
                        description={t('leaveRequest.deleteInfo')}
                        onNo={() => setDeleteModalVisible(false)}
                        onYes={() => {
                            setDeleteModalVisible(false);
                            deleteLeaveRequest();
                        }}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default NotificationInfo;
